import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Inject,
  Input,
  LOCALE_ID,
  OnChanges,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import {
  DprDetailsGridBaseComponent,
  DprStatusTrackerGridData,
} from '@dpr-details/models/grid';
import {
  getFormulaInputColumnIds,
  getStatusTrackerFormulas,
  isStatusTrackerColumnCarryOverEnabled,
  isStatusTrackerColumnDerived,
  getDerivedColumnOptions,
} from '@dpr-details/utils';
import {
  DateDisplayComponent,
  DateEditComponent,
  FilterSelectEditComponent,
  FilterTextEditComponent,
  NumericEditComponent,
  SelectDisplayComponent,
  SelectEditComponent,
  TextDisplayComponent,
  TextEditComponent,
} from '@grid/components';
import { GridAlignment, GridCellContentAlignment } from '@grid/enums';
import {
  CellEditConfig,
  ColorOptions,
  FilterColumn,
  FilterControls,
  GridApi,
  GridColumn,
  GridOptions,
  GridStackedHeaderColumn,
  GridStackedHeaderRow,
  SelectionItem,
  TooltipConfig,
} from '@grid/models';
import { Constants as GridConstants } from '@grid/utils/constants';
import { StaticMessage, UtcDateTimeOptions } from '@shared/enums';
import { ColorType, ExtendedDataType } from '@shared/models';
import {
  StatusTrackerColumn,
  StatusTrackerColumnDataTypeValue,
  StatusTrackerWithData,
} from '@shared/models/project-setup';
import {
  Constants,
  buildBooleanValues,
  getFormulaDisplayExpression,
  getStaticMessage,
  isConfigurationDataType,
  isDecimalDataType,
  toUTCDate,
} from '@shared/utils';
import {
  GridDefaultLabels,
  getDecimalCellDisplay,
  getDecimalCellFilterValueFn,
} from '@shared/utils/grid';
import { selectTooltipMessage } from '@grid/utils/select';
import { BooleanTerminologyType } from '@shared/enums/boolean-terminology';

@Component({
  selector: 'app-dpr-status-tracker-grid',
  templateUrl: './dpr-status-tracker-grid.component.html',
  styleUrls: ['./dpr-status-tracker-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DprStatusTrackerGridComponent
  extends DprDetailsGridBaseComponent<DprStatusTrackerGridData>
  implements OnChanges
{
  //#region Inputs

  @Input({ required: true }) statusTracker: StatusTrackerWithData;
  @Input({ required: true }) colorTypes: ColorType[];

  //#endregion

  @Output() formulaInputEdited = new EventEmitter<DprStatusTrackerGridData>();

  @ViewChild('timeComponent', { static: true })
  timeComponent: TemplateRef<any>;

  lastDprId: string | number = -1;
  gridStackedHeaderRows$ = new BehaviorSubject<GridStackedHeaderRow[]>(null);
  gridFilterControls$ = new BehaviorSubject<
    FilterControls<DprStatusTrackerGridData>
  >(null);
  gridApi: GridApi<DprStatusTrackerGridData>;
  constructor(
    private datePipe: DatePipe,
    @Inject(LOCALE_ID) private locale: string
  ) {
    super();
  }

  get colorOptions(): ColorOptions {
    return {
      allowColorPicker: true,
      colorPickerColumnDef: 'cellColors',
      colorPalette: this.colorTypes,
    };
  }

  //#region Lifecycle Hooks

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.statusTracker &&
      changes.statusTracker.currentValue?.name !==
        changes.statusTracker.previousValue?.name
    ) {
      this.gridApi?.refreshFilters();
    }

    if (
      (changes.canEdit || changes.selectedSubproject || changes.gridData) &&
      this.statusTracker
    ) {
      // refresh filters if status tracker has changes or if the column structure for the
      // current status tracker has changed
      this.initializeGrid(
        changes.statusTracker &&
          (changes.statusTracker.currentValue?.name !==
            changes.statusTracker.previousValue?.name ||
            // only the columns and rowGroups need to be checked
            // to avoid unnecessary filter clears as those two are the only elements
            // that when changed affect the filters
            JSON.stringify(changes.statusTracker.currentValue?.columns) !==
              JSON.stringify(changes.statusTracker.previousValue?.columns) ||
            JSON.stringify(changes.statusTracker.currentValue?.rowGroups) !==
              JSON.stringify(changes.statusTracker.previousValue?.rowGroups))
      );
    }
  }

  //#endregion

  initializeGrid(refreshFilters: boolean): void {
    this.gridOptions$.next(this.getGridOptions());
    this.gridColumns$.next(this.getGridColumns());
    // compute filters only when the initial value is null or the refreshFilters parameter is true
    if (refreshFilters || this.gridFilterControls$.value === null) {
      this.gridFilterControls$.next(this.getGridFilters());
    }
    this.gridStackedHeaderRows$.next(this.getGridHeaderRows());
  }

  protected getGridOptions(): GridOptions<DprStatusTrackerGridData> {
    return {
      sequenceConfig: {
        disabled: true,
      },
      labeling: GridDefaultLabels,
      addConfig: {
        allowAdding: false,
        keepAutogeneratedIds: true,
      },
      editConfig: {
        allowEditing: this.canEdit,
      },
      deleteConfig: {
        allowDeleting: false,
      },
      saveConfig: {
        isExternalSave: true,
      },
      rowConfig: {
        allowDeleting: false,
        allowSelection: false,
      },
      rowGroupConfig: {
        enableRowGrouping: true,
        columnDef: 'groupId',
        customHeaderClass: 'border-right-none',
        groups: this.statusTracker.rowGroups
          .filter(
            (x) =>
              x.isActive &&
              this.statusTracker.rows
                .filter((r) => r.rowGroupId === x.id && r.isActive)
                .some((isActive) => isActive)
          )
          .map((x) => ({
            id: x.id,
            name: x.name,
          })),
        getGroupName: (groupId: number) =>
          this.statusTracker?.rowGroups.find((x) => x.id === groupId)?.name,
      },
      selectionConfig: {
        allowSelection: false,
      },
      sortConfig: {
        allowSorting: false,
      },
      colorConfig: {
        allowColorPicker: true,
        colorPickerColumnDef: 'cellColors',
        colorPalette: this.colorTypes,
      },
      events: {
        onGridRegistered: (gridApi) => {
          this.onGridRegistered(gridApi);
          this.gridApi = gridApi;
        },
      },
    };
  }

  protected getGridColumns(): GridColumn<DprStatusTrackerGridData>[] {
    const statusTrackerColumns = this.statusTracker.columns;
    const statusTrackerColumnDataTypeValues =
      this.statusTracker.columnDataTypeValues;

    const gridColumns: GridColumn<DprStatusTrackerGridData>[] = [
      {
        columnDef: 'row',
        header: {
          alignment: GridAlignment.Left,
        },
        columnAlignment: GridAlignment.Left,
        width: 320,
        tabIndex: -1,
        disableFocusedBackground: true,
        display: {
          type: TextDisplayComponent,
        },
        allowEditing: false,
      },
    ];

    const formulas = getStatusTrackerFormulas(this.statusTracker);
    const formulaInputIds = getFormulaInputColumnIds(formulas);

    for (const column of statusTrackerColumns) {
      const isCarryOver = isStatusTrackerColumnCarryOverEnabled(
        this.statusTracker,
        column
      );
      const isFormula = column.dataTypeId === ExtendedDataType.Formula;

      const newColumn: GridColumn<DprStatusTrackerGridData> = {
        columnDef: column.name,
        header: {
          name: column.name,
          icon: isFormula ? 'function-variant' : undefined,
          tooltip: isFormula
            ? getFormulaDisplayExpression(
                formulas.find((formula) => formula.columnId === +column.id)
                  ?.expression,
                statusTrackerColumns
              )
            : undefined,
          alignment: this.shouldLeftAlign(column.dataTypeId)
            ? GridAlignment.Left
            : GridAlignment.Right,
        },
        columnAlignment: this.shouldLeftAlign(column.dataTypeId)
          ? GridAlignment.Left
          : GridAlignment.Right,
        minWidth: column.dataTypeId === ExtendedDataType.Datetime ? 160 : 110,
        customClassList: this.getCellElementClasses(column),
        display: (data: DprStatusTrackerGridData) => {
          if (isConfigurationDataType(column.dataTypeId)) {
            if (data.value) {
              data.value = +data.value;
            }
          }

          switch (column.dataTypeId) {
            case ExtendedDataType.List:
              return {
                type: SelectDisplayComponent,
                config: {
                  options: this.selectionItemsForColumn(
                    +column.id,
                    statusTrackerColumnDataTypeValues
                  ),
                },
              };
            case ExtendedDataType.Boolean:
              return {
                type: SelectDisplayComponent,
                config: {
                  options: buildBooleanValues(
                    column.booleanTerminologyId ??
                      BooleanTerminologyType.Default
                  ).map((v) => ({ id: v.id, name: v.value })),
                },
              };
            case ExtendedDataType.Date:
              return {
                type: DateDisplayComponent,
              };
            case ExtendedDataType.Datetime:
              return {
                type: DateDisplayComponent,
                config: {
                  format: GridConstants.DefaultValues.dateDisplayFormatWithTime,
                },
              };
            default:
              return isDecimalDataType(column.dataTypeId)
                ? getDecimalCellDisplay(column.dataTypeId)
                : { type: TextDisplayComponent };
          }
        },
        edit: (data: DprStatusTrackerGridData) =>
          this.computeCellEditConfig(column, data),
        allowEditing: this.canEdit,
        tooltip: this.getTooltip(
          column.id,
          column.dataTypeId,
          column.booleanTerminologyId,
          statusTrackerColumnDataTypeValues
        ),
        ...(formulaInputIds.has(+column.id) && !isCarryOver
          ? {
              onCellEdited: (data: DprStatusTrackerGridData) =>
                this.formulaInputEdited.emit(data),
              recomputeRowOnChange: true,
            }
          : {}),
      };

      gridColumns.push({
        ...newColumn,
        ...getDerivedColumnOptions(
          isStatusTrackerColumnDerived(this.statusTracker, column)
        ),
      });
    }

    return gridColumns;
  }

  private getTooltip(
    columnId: number | string,
    dataTypeId: number,
    booleanTerminologyId: BooleanTerminologyType,
    statusTrackerColumnDataTypeValues: StatusTrackerColumnDataTypeValue[]
  ): TooltipConfig<DprStatusTrackerGridData> {
    switch (dataTypeId) {
      case ExtendedDataType.List: {
        return {
          message: selectTooltipMessage(
            this.selectionItemsForColumn(
              +columnId,
              statusTrackerColumnDataTypeValues
            )
          ),
        };
      }
      case ExtendedDataType.Boolean: {
        return {
          message: selectTooltipMessage(
            buildBooleanValues(booleanTerminologyId)
          ),
        };
      }
      default: {
        return null;
      }
    }
  }

  private getGridFilters(): FilterControls<DprStatusTrackerGridData> {
    const filterControls: FilterControls<DprStatusTrackerGridData> = {
      id: this.statusTracker.name,
      columns: [],
      placeholder: getStaticMessage(StaticMessage.AllLabel),
    };

    const statusTrackerColumns = this.statusTracker.columns;

    filterControls.columns.push({
      columnDef: 'row',
      edit: {
        type: FilterTextEditComponent,
        config: {
          autofocus: false,
          alignment: GridCellContentAlignment.Left,
          options: {
            maxLength: Constants.FieldLimits.maxTextEditComponentLimit,
          },
        },
      },
    });

    for (const column of statusTrackerColumns) {
      filterControls.columns.push({
        columnDef: column.name,
        edit: this.computeCellEditConfig(column, undefined, true),
        computeFilterDataValueFn: this.getFilterDataValueFn(column),
      });
    }

    return filterControls;
  }

  /** A filter matches a cell's value as it is shown (e.g. a stored 5 is found by typing 5.00). */
  private getFilterDataValueFn(
    column: StatusTrackerColumn
  ): FilterColumn['computeFilterDataValueFn'] {
    if (isDecimalDataType(column.dataTypeId)) {
      return getDecimalCellFilterValueFn(column.dataTypeId, this.locale);
    }

    if (
      column.dataTypeId === ExtendedDataType.Date ||
      column.dataTypeId === ExtendedDataType.Datetime
    ) {
      const isDate = column.dataTypeId === ExtendedDataType.Date;
      const utcOption = isDate
        ? UtcDateTimeOptions.UtcDateWithoutTime
        : UtcDateTimeOptions.UtcDateWithTime;
      const format = isDate
        ? GridConstants.DefaultValues.dateDisplayFormat
        : GridConstants.DefaultValues.dateDisplayFormatWithTime;

      return (val) =>
        this.datePipe.transform(toUTCDate(new Date(val), utcOption), format);
    }

    return undefined;
  }

  private getCellElementClasses(column: StatusTrackerColumn): string[] {
    const groupColumns = this.statusTracker.columns.filter(
      (x) => x.columnGroupId === column.columnGroupId
    );
    return groupColumns.indexOf(column) !== groupColumns.length - 1
      ? ['border-right-none']
      : null;
  }

  private getGridHeaderRows(): GridStackedHeaderRow[] {
    const statusTrackerColumnGroups = this.statusTracker.columnGroups;
    const statusTrackerColumns = this.statusTracker.columns;
    const stackedHeaderColumns: GridStackedHeaderColumn[] = [
      {
        columnDef: 'spacer-stacked-header',
        headerText: '',
        customHeaderClass: 'border-bottom-none',
        alignment: GridAlignment.Center,
        colspan: 2,
      },
    ];

    for (const columnGroup of statusTrackerColumnGroups) {
      const colCount = statusTrackerColumns.filter(
        (x) => x.columnGroupId === columnGroup.id
      ).length;

      if (!colCount) {
        continue;
      }

      stackedHeaderColumns.push({
        columnDef: `group__${columnGroup.name}`,
        headerText: columnGroup.name,
        alignment: GridAlignment.Center,
        colspan: colCount,
      });
    }
    return [{ stackedHeaderColumns }];
  }

  private selectionItemsForColumn(
    columnId: number,
    values: StatusTrackerColumnDataTypeValue[]
  ): SelectionItem[] {
    return values
      .filter((cd) => cd.columnId === columnId)
      .map((data) => ({
        id: data.id,
        name: data.value,
      }));
  }

  private computeCellEditConfig(
    column: StatusTrackerColumn,
    data?: DprStatusTrackerGridData,
    isFilter: boolean = false
  ): CellEditConfig<DprStatusTrackerGridData> {
    const statusTrackerColumnDataTypeValues =
      this.statusTracker.columnDataTypeValues;

    if (isConfigurationDataType(column.dataTypeId)) {
      if (data && data[column.name]) {
        data[column.name] = +data[column.name];
      }
    }

    switch (column.dataTypeId) {
      case ExtendedDataType.List:
        return {
          type: isFilter ? FilterSelectEditComponent : SelectEditComponent,
          allowEditing: isFilter ? true : this.canEdit,
          config: {
            alignment: GridCellContentAlignment.Left,
            autoOpen: !isFilter,
            autofocus: !isFilter,
            options: this.selectionItemsForColumn(
              +column.id,
              statusTrackerColumnDataTypeValues
            ),
            includeNoneOption: !isFilter,
            noneOptionName: getStaticMessage(StaticMessage.NoneOptionName),
            colorOptions: !isFilter ? this.colorOptions : undefined,
          },
        };
      case ExtendedDataType.Boolean:
        return {
          type: isFilter ? FilterSelectEditComponent : SelectEditComponent,
          allowEditing: isFilter ? true : this.canEdit,
          config: {
            alignment: GridCellContentAlignment.Left,
            autoOpen: !isFilter,
            autofocus: !isFilter,
            options: buildBooleanValues(
              column.booleanTerminologyId ?? BooleanTerminologyType.Default
            ).map((v) => ({ id: v.id, name: v.value })),
            includeNoneOption: !isFilter,
            noneOptionName: getStaticMessage(StaticMessage.NoneOptionName),
            colorOptions: !isFilter ? this.colorOptions : undefined,
          },
        };
      case ExtendedDataType.Number:
      case ExtendedDataType.Percentage:
        return {
          type: isFilter ? FilterTextEditComponent : NumericEditComponent,
          allowEditing: isFilter
            ? true
            : this.canEdit &&
              !isStatusTrackerColumnCarryOverEnabled(
                this.statusTracker,
                column
              ),
          config: isFilter
            ? {
                autofocus: false,
                alignment: GridCellContentAlignment.Left,
                options: {
                  maxLength: Constants.FieldLimits.maxTextEditComponentLimit,
                },
              }
            : {
                autofocus: !isFilter,
                options: {
                  allowSign: false,
                  allowDecimals: true,
                  allowPercentage:
                    column.dataTypeId === ExtendedDataType.Percentage,
                  decimalPrecision: data?.decimalCount ?? 2,
                  step: 0.01,
                  min: 0,
                  colorOptions: !isFilter ? this.colorOptions : undefined,
                },
              },
        };
      case ExtendedDataType.Date:
        return {
          type: isFilter ? FilterTextEditComponent : DateEditComponent,
          config: isFilter
            ? {
                autofocus: false,
                alignment: GridCellContentAlignment.Left,
                options: {
                  maxLength: Constants.FieldLimits.maxTextEditComponentLimit,
                },
              }
            : {
                colorOptions: this.colorOptions,
                saveButtonLabel: getStaticMessage(StaticMessage.SaveButton),
                clearButtonLabel: getStaticMessage(StaticMessage.ClearButton),
                timeOptions: {
                  withoutTimeZone: true,
                },
              },
          allowEditing: isFilter ? true : this.canEdit,
        };
      case ExtendedDataType.Datetime:
        return {
          type: isFilter ? FilterTextEditComponent : DateEditComponent,
          config: isFilter
            ? {
                autofocus: false,
                alignment: GridCellContentAlignment.Left,
                options: {
                  maxLength: Constants.FieldLimits.maxTextEditComponentLimit,
                },
              }
            : {
                colorOptions: this.colorOptions,
                saveButtonLabel: getStaticMessage(StaticMessage.SaveButton),
                clearButtonLabel: getStaticMessage(StaticMessage.ClearButton),
                timeOptions: {
                  allowTimePicker: true,
                  timePickerComponent: this.timeComponent,
                  withoutTimeZone: true,
                },
                format: GridConstants.DefaultValues.dateDisplayFormatWithTime,
              },
          allowEditing: isFilter ? true : this.canEdit,
        };
      case ExtendedDataType.Formula:
        return {
          type: isFilter ? FilterTextEditComponent : undefined,
          config: isFilter
            ? {
                autofocus: false,
                alignment: GridCellContentAlignment.Left,
                options: {
                  maxLength: Constants.FieldLimits.maxTextEditComponentLimit,
                },
              }
            : undefined,
          allowEditing: isFilter ? true : false,
        };
      default:
        return {
          type: isFilter ? FilterTextEditComponent : TextEditComponent,
          config: {
            autofocus: !isFilter,
            alignment: GridCellContentAlignment.Left,
            colorOptions: !isFilter ? this.colorOptions : undefined,
            options: {
              maxLength: Constants.FieldLimits.maxFreeTextDataTypeLimit,
            },
          },
          allowEditing: isFilter ? true : this.canEdit,
        };
    }
  }

  private shouldLeftAlign(dataTypeId: number): boolean {
    return (
      dataTypeId === ExtendedDataType.FreeText ||
      dataTypeId === ExtendedDataType.List ||
      dataTypeId === ExtendedDataType.Boolean
    );
  }
}
